/**
 * One shape for a listing row, whatever the build fetched it from.
 *
 * The public API returns snake_case (`space_details`, `seller_rating`,
 * `verification_status`), while the local fixture files and older dumps use
 * camelCase (`spaceDetails`, `rating`). scripts/prerender-listings.mjs read
 * only the camelCase spelling, so against the live API every one of these came
 * back undefined — and the prerendered JSON-LD shipped with nothing but name,
 * url, address and description. geo, floorSize, maximumAttendeeCapacity,
 * amenityFeature, openingHoursSpecification and the seating layouts were all
 * silently dropped, which is exactly the data llms.txt advertises to AI
 * clients. The React app was unaffected because its own mapper handles
 * snake_case, so the page looked complete in a browser and was hollow for
 * every crawler that does not run JavaScript.
 *
 * Normalising once, here, is what keeps that from happening again: the
 * builders downstream see one spelling.
 */

/** Pick the first defined value among several candidate keys. */
function pick(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

/** `space_details` arrives as an object or as a JSON string, depending on source. */
function parseJsonish(value) {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * Drop repeated entries from a tag list, keeping first-seen order.
 *
 * Stored `space_details.amenities` can carry the same chip twice (Kaviareň
 * Kapucíno shipped `["…", "DJ", "Stolný futbal", "DJ", "Bar"]`), and every
 * renderer — the detail page, the JSON-LD amenityFeature, the Markdown twin —
 * printed it twice. Comparison is trimmed and case-insensitive; the first
 * spelling wins.
 *
 * @param {unknown} list
 * @returns {unknown} a deduplicated array, or the input unchanged if not an array
 */
export function uniqueList(list) {
  if (!Array.isArray(list)) return list;
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const key = typeof item === 'string' ? item.trim().toLowerCase() : item;
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/**
 * `space_details` with its tag lists (amenities, eventTypes) deduplicated.
 * Returns the input unchanged when there is nothing to fix.
 *
 * @template T
 * @param {T} details
 * @returns {T}
 */
export function dedupeSpaceDetailLists(details) {
  if (!details || typeof details !== 'object') return details;
  const next = { ...details };
  for (const key of ['amenities', 'eventTypes']) {
    if (Array.isArray(next[key])) next[key] = uniqueList(next[key]);
  }
  return next;
}

/**
 * @param {object} row raw listing row from the API or a fixture file
 * @returns {object} the same row plus normalized camelCase aliases
 */
export function normalizeListingRow(row) {
  if (!row || typeof row !== 'object') return row;

  const spaceDetails = dedupeSpaceDetailLists(parseJsonish(pick(row, 'spaceDetails', 'space_details')));

  return {
    ...row,
    spaceDetails,
    slug: pick(row, 'slug'),
    priceOnRequest: pick(row, 'priceOnRequest', 'price_on_request'),
    priceUnit: pick(row, 'priceUnit', 'price_unit'),
    // The rating on a venue profile is the venue's GOOGLE rating, which lives
    // in space_details — NOT `seller_rating`, which rates the account that
    // posted the listing and is 0 for every editorially compiled profile.
    // utils/seoHelpers.ts reads googleRating for the same reason; taking the
    // seller column here would have produced an empty aggregateRating on every
    // page while the UI displayed 4.2 from 50 reviews.
    rating: Number(pick(row, 'rating') ?? spaceDetails?.googleRating ?? 0) || undefined,
    ratingCount: Number(pick(row, 'ratingCount') ?? spaceDetails?.googleRatingsCount ?? 0) || undefined,
    isAdminListing: Boolean(Number(pick(row, 'isAdminListing', 'is_admin_listing') || 0)),
    managedMode: pick(row, 'managedMode', 'managed_mode'),
    // Provenance columns (migrations/20260715_add_trust_columns.sql).
    source: pick(row, 'source'),
    claimedByOwner: (() => {
      const raw = pick(row, 'claimedByOwner', 'claimed_by_owner');
      return raw == null ? null : Boolean(Number(raw));
    })(),
    verificationStatus: pick(row, 'verificationStatus', 'verification_status'),
    lastVerifiedAt: pick(row, 'lastVerifiedAt', 'last_verified_at'),
  };
}

/**
 * The `trust` object shape that utils/listingTrust.mjs expects, built from a
 * normalized row.
 *
 * @param {object} row normalized listing row
 */
export function trustFromRow(row) {
  return {
    source: row?.source ?? null,
    claimedByOwner: row?.claimedByOwner ?? null,
    verificationStatus: row?.verificationStatus ?? null,
    lastVerifiedAt: row?.lastVerifiedAt ?? null,
  };
}
